import { baseApi } from "@/shared/api";
import type { SigningActionResponse } from "@/shared/types";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type {
  PlatformAccessApiPayload,
  PlatformAccessResponse,
  PlatformAccessContract,
  PlatformAccessListResponse,
} from "../types";

export const platformAccessApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    submitPlatformAccess: builder.mutation<PlatformAccessResponse, PlatformAccessApiPayload>({
      query: (body) => ({
        url: "/api/legal/platform-access/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "PlatformAccess" as const, id: "LIST" }],
    }),

    updatePlatformAccess: builder.mutation<PlatformAccessResponse, { id: number; data: PlatformAccessApiPayload }>({
      query: ({ id, data }) => ({
        url: `/api/legal/platform-access/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PlatformAccess" as const, id },
        { type: "PlatformAccess" as const, id: "LIST" },
      ],
    }),

    getPlatformAccess: builder.query<PlatformAccessResponse, number>({
      query: (id) => ({
        url: `/api/legal/platform-access/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "PlatformAccess" as const, id }],
    }),

    getPlatformAccessList: builder.query<PlatformAccessContract[], void>({
      query: () => ({
        url: "/api/legal/platform-access/",
        method: "GET",
      }),
      transformResponse: (response: PlatformAccessListResponse): PlatformAccessContract[] => response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "PlatformAccess" as const, id })),
              { type: "PlatformAccess" as const, id: "LIST" },
            ]
          : [{ type: "PlatformAccess" as const, id: "LIST" }],
    }),

    submitPlatformAccessForSigning: builder.mutation<SigningActionResponse, { id: number; cms_signature: string }>({
      query: ({ id, cms_signature }) => ({
        url: `/api/legal/platform-access/${id}/submit-for-signing/`,
        method: "POST",
        body: { cms_signature },
        credentials: "include" as const,
      }),
      invalidatesTags: () => [
        { type: "PlatformAccess" as const, id: "LIST" },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          platformAccessApi.util.updateQueryData("getPlatformAccess", id, (draft) => {
            draft.signing_status = 1;
            draft.status = "pending_signing";
          }),
        );
      },
    }),

    downloadPlatformAccessPreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/legal/platform-access/${id}/preview/`,
        method: "GET",
        responseHandler: fileResponseHandler,
      }),
    }),

    deletePlatformAccess: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/platform-access/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PlatformAccess" as const, id },
        { type: "PlatformAccess" as const, id: "LIST" },
      ],
    }),

    revokePlatformAccess: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/platform-access/${id}/revoke/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PlatformAccess" as const, id },
        { type: "PlatformAccess" as const, id: "LIST" },
        "PendingActions",
      ],
    }),
  }),
});

export const {
  useSubmitPlatformAccessMutation,
  useUpdatePlatformAccessMutation,
  useGetPlatformAccessQuery,
  useGetPlatformAccessListQuery,
  useSubmitPlatformAccessForSigningMutation,
  useDownloadPlatformAccessPreviewMutation,
  useDeletePlatformAccessMutation,
  useRevokePlatformAccessMutation,
} = platformAccessApi;
